import React from 'react';
import ReactDOM from 'react-dom/client';
import './setupMonaco';
import './i18n';
import './index.css';
import './styles/appZoom.css';
import App from './App';
import VerifyEmailPage from './components/auth/VerifyEmailPage';
import WebAuthCallback from './components/auth/WebAuthCallback';
import { isWebApp } from './platform/target';
import { isWebCallback } from './services/webOidc';
import AppErrorBoundary from './components/AppErrorBoundary';
import CrashFallback from './components/CrashFallback';
import { startApp, startupErrorDetail } from './appStartup';
import reportWebVitals from './reportWebVitals';
import { initPreferencesFromDisk } from './store/preferencesStore';
import { initRegionPicker } from './services/regionService';
import { startDocumentAttachmentUploader } from './services/documentAttachmentService';
// The documents store's HTML <-> blocks conversion (bodyHtmlCodec.js).
import './extensions/bundled/documents/registerBodyHtmlCodec';
import { preloadLexicalEditor } from './components/ui/LexicalRichEditorLazy';
import { suppressStrayPrint } from './utils/nativePrint';
import { installWebRecords } from './records/web/installWebRecords';
import { installDocumentTabWorkspaces } from './store/documentTabWorkspaces';
import { installDocumentTabsPersist } from './store/documentTabsPersist';

// Suppress a STRAY browser print (Ctrl+P would otherwise print the whole app
// chrome). The real function is captured inside utils/nativePrint before this
// runs — an outright `window.print = () => {}` made printing impossible on
// Windows, where wry implements the Rust print command as `eval("window.print()")`
// and therefore lands right back here. See that module for the platform table.
suppressStrayPrint();

// Monaco throws a "Canceled" CancellationError when a model is disposed while the TypeScript
// language worker has a pending async operation (e.g. switching requests mid-flight).
// These are harmless internal signals — suppress them so they don't surface as runtime errors.
// Matches on NAME as well as message: Monaco's CancellationError carries
// `name: 'Canceled'`, and React 19 re-throws effect-cleanup errors through
// `reportError`, where a wrapped or re-tagged message would slip past a
// message-only check. `stopImmediatePropagation` so the dev overlay's own
// listener does not paint it either — this is an internal disposal signal, not
// a fault, and it surfaced on every tab switch away from a large response.
const isCanceled = (err) => err && (err.name === 'Canceled' || err.message === 'Canceled');
window.addEventListener('error', (e) => {
  if (isCanceled(e?.error)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  if (isCanceled(e?.reason)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);

// Non-macOS Tauri: WebView2 (etc.) shows white where the shell is transparent behind rounded UI.
(function initTauriOpaqueShell() {
  if (typeof window === 'undefined' || !document.documentElement) return;
  const ua = navigator.userAgent || '';
  const inTauri =
    window.__TAURI__ !== undefined ||
    window.__TAURI_INTERNALS__ !== undefined ||
    ua.includes('Tauri');
  const isMacDesktop = /Mac|Macintosh/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua);
  if (inTauri && !isMacDesktop) {
    document.documentElement.classList.add('tauri-shell-opaque');
  }
})();

// The anonymous install ping is gone — a persistent id on the user's device
// needs consent under ePrivacy, a plain update request doesn't. Drop the keys
// existing installs still carry so no stale UUID lingers.
(function purgeLegacyTelemetryKeys() {
  try {
    localStorage.removeItem('apiboo.telemetry.id');
    localStorage.removeItem('apiboo.telemetry.last');
    localStorage.removeItem('apiboo.telemetry.optout');
  } catch {
    // Storage may be unavailable — nothing to clean up then.
  }
})();

const root = ReactDOM.createRoot(document.getElementById('root'));

async function bootstrap() {
  // Web app: boards, documents, calendar and projects read from the server
  // (records/web). Before anything can read a record. No-op on desktop.
  installWebRecords();
  // Applies the cached WS region synchronously, then latency-probes in the
  // background — must run before any Phoenix socket connects.
  initRegionPicker();
  // Resume queued document-media uploads (persistent outbox) + arm the
  // online/sign-in triggers. Idempotent; all work is background.
  startDocumentAttachmentUploader();
  // Document tabs: one set per Documents workspace, restored across restarts.
  installDocumentTabWorkspaces();
  installDocumentTabsPersist();
  // Desktop: a connected scope's git folder follows its documents (git sync
  // Stage 2). Loaded on its own — git and the converter stay out of startup.
  if (!isWebApp()) {
    import('./services/git/scopeGitAutoRefresh')
      .then(async (m) => {
        m.startScopeGitAutoRefresh();
        // Every tracked workspace's and scope's state, without visiting the Git page.
        (await import('./services/git/gitStatusPoller')).startGitStatusPoller({ touchAllScopes: m.touchAllLinked });
      })
      .catch((err) => console.warn('[git] scope auto-refresh not started:', err));
  }
  await initPreferencesFromDisk();
}

// Two independent failure modes, two independent guards — see
// appStartup.js and components/AppErrorBoundary.js for why neither covers
// the other. `bootstrap()` rejecting used to skip root.render entirely and
// leave an empty `#root`; a render error inside the app used to unmount the
// whole tree and leave the same white window.
startApp({
  boot: bootstrap,
  renderApp: () => {
    const isVerifyPage = window.location.pathname === '/verify-email';
    // Web app only: auth.apiboo.com sends the browser back here after sign-in.
    const isWebAuthCallback = isWebApp() && isWebCallback();
    root.render(
      <React.StrictMode>
        {/*
          Inside StrictMode but OUTSIDE the app: a boundary nested any deeper
          could itself be unmounted by the error it is meant to catch, and one
          placed outside StrictMode would not see the double-invoked render
          that surfaces effect-order bugs in development.
        */}
        <AppErrorBoundary>
          {isWebAuthCallback ? <WebAuthCallback /> : isVerifyPage ? <VerifyEmailPage /> : <App />}
        </AppErrorBoundary>
      </React.StrictMode>
    );
  },
  renderFallback: (error) =>
    root.render(
      <CrashFallback
        title="Apiboo could not start"
        message="Something failed while loading your settings, so the app stopped before it could open."
        detail={startupErrorDetail(error)}
      />
    ),
});

// Warm the Lexical editor chunk during startup idle time so the first
// task-card / task-modal edit renders the editor instantly.
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => preloadLexicalEditor(), { timeout: 3000 });
} else {
  setTimeout(preloadLexicalEditor, 1500);
}

reportWebVitals();
